import { getNormFromMap, projectLinesToViewport } from "@/utils/viewportHelper";
import React, { useEffect, useState, useCallback, useMemo } from "react";

import * as turf from "@turf/turf";

import runPlanrouteAnalyser from "@/features/voyagePlanner/runPlanrouteAnalyser";
import { legtypeOf } from "@/features/voyagePlanner/manualSketch";
import L from "leaflet";
import { GeoJSON, LayerGroup, Marker, useMap } from "react-leaflet";
import RouteHelper, {
  hasWaypointWarnings,
  hasWaypointInfos,
  findPlannedStateColor,
  findRoutePane,
  ROUTE_PANES,
  PATHFINDER_COLOR,
} from "../../utils/routeHelper";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useAccessContext } from "../../contexts/AccessContext";
import {
  useIsCalculating,
  useRouteStore,
} from "@/features/route/state/RouteStore.jsx";
import { normalizeRenderedRoute } from "@/features/route/state/RouteAdapters.jsx";
import { selectVesselMeta } from "@/features/route/state/RouteSelectors.jsx";
import { resolveWeatherLimits } from "@/features/vessel/vesselMeta.js";
import { insertWaypointIcon } from "@/features/route/waypointIcons.jsx";

dayjs.extend(utc);
dayjs.extend(localizedFormat);

// Find the closest point on a polyline to the cursor — in screen-pixel
// space, not lon/lat.
const findClosestPointOnLine = (latlng, line, map) => {
  const coords = line?.geometry?.coordinates ?? [];
  if (coords.length < 2 || !map) return latlng;

  const cursor = map.latLngToContainerPoint(latlng);
  const points = coords.map(([lng, lat]) =>
    map.latLngToContainerPoint(L.latLng(lat, lng)),
  );

  let bestPoint = points[0];
  let bestDistSq = Infinity;

  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    let t = 0;
    if (lenSq > 0) {
      t = ((cursor.x - a.x) * dx + (cursor.y - a.y) * dy) / lenSq;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
    }
    const px = a.x + t * dx;
    const py = a.y + t * dy;
    const distSq = (cursor.x - px) ** 2 + (cursor.y - py) ** 2;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      bestPoint = L.point(px, py);
    }
  }

  return map.containerPointToLatLng(bestPoint);
};

export default function RouteLines({
  vesselRoutes,
  mapBounds,
  calculatorDepartureTime,
  calculatorBasespeed,
  setEtaResponse,
  speedProfileData,
  consumptionProfileData,
  setRouteConsumption,
  setPathfinderResponse,
  setEtaError,
  norm,
  viewportTick,
  plannerVesselSettings,
  cwViaAreasGeojson,
  applyRnwSafety,
  applyEca,
  avoidHra,
  avoidJwla,
  avoidCaa,
  plannerDraftRouteId = null,
}) {
  const { accessContext, setAccessContext } = useAccessContext();
  const { state: routeState, replaceRoute, setIsCalculating } = useRouteStore();
  const isCalculating = useIsCalculating();

  const map = useMap();
  for (const [name, z] of Object.entries(ROUTE_PANES)) {
    if (!map.getPane(name)) map.createPane(name);
    map.getPane(name).style.zIndex = z;
  }

  const [hoverMarker, setHoverMarker] = useState(null); // Track the position of the marker to display

  // Clear hover marker whenever route geometry changes (e.g., point deleted via right-click)
  useEffect(() => {
    setHoverMarker(null);
  }, [vesselRoutes, mapBounds, viewportTick]);

  const getStyle = useCallback(({ feature, routeType, state, isStroke, sketch = false }) => {
    // Dim the Pathfinder candidate route while a recalc is in flight to
    // signal "busy" alongside the disabled edit handlers.
    const candidateBusy = isCalculating && routeType === "Pathfinder";
    if (routeType === "reported") {
      let dashArray =
        feature.properties.analyse.wp2.properties &&
        feature.properties.analyse.wp2.properties.analyse
          .is_gap_time_by_timestamps
          ? "8,8"
          : "0";
      return {
        color: isStroke ? "white" : RouteHelper.findRouteColor(routeType),
        weight: isStroke ? 6 : 4,
        opacity: isStroke ? 1 : 0.6,
        dashArray: dashArray,
        pane: findRoutePane("line", "reported", null),
        stroke: true,
      };
    } else if (routeType === "planned") {
      const wp1Props = feature.properties?.analyse?.wp1?.properties;
      const segmentWarned = hasWaypointWarnings(wp1Props);
      const segmentInfo = !segmentWarned && hasWaypointInfos(wp1Props);

      return {
        color: isStroke
          ? "white"
          : segmentWarned
            ? "#d32f2f"
            : segmentInfo
              ? "#ed6c02"
              : findPlannedStateColor(state),
        weight: isStroke ? 6 : 4,
        opacity: isStroke ? 1 : 0.6,
        dashArray: segmentWarned || segmentInfo ? "8,8" : "0",
        pane: findRoutePane("line", "planned", state),
        stroke: true,
      };
    } else if (routeType === "Pathfinder") {
      const wp1Props = feature.properties?.analyse?.wp1?.properties;
      const segmentWarned = hasWaypointWarnings(wp1Props);
      const segmentInfo = !segmentWarned && hasWaypointInfos(wp1Props);

      return {
        color: isStroke
          ? "white"
          : segmentWarned
            ? "#d32f2f"
            : segmentInfo
              ? "#ed6c02"
              : PATHFINDER_COLOR,
        weight: isStroke ? 6 : 4,
        opacity: candidateBusy ? (isStroke ? 0.4 : 0.25) : isStroke ? 1 : 0.6,
        dashArray: segmentWarned || segmentInfo || sketch ? "8,8" : "0",
        pane: findRoutePane("line", "Pathfinder", null),
        stroke: true,
      };
    }
  }, [isCalculating]);

  // Add event handlers for mouseover to show marker
  const handleMouseMove = (e, feature) => {
    const latlng = e.latlng;
    const closestPoint = findClosestPointOnLine(latlng, feature, map);
    setHoverMarker(closestPoint); // Set the hover marker at the closest point on the line
  };

  const handleMouseOut = () => {
    setHoverMarker(null); // Remove the marker when the cursor leaves the line
  };

  // Handle click event to add a point on the line
  const handleLineClick = (e, lineSegment, route) => {
    // Ignore inserts while a recalc is in flight to prevent racing
    // requests from clobbering each other.
    if (isCalculating) return;
    const features = route.points.geojson.features;
    if (features.length < 2) return;

    // Identify the clicked leg by its endpoint waypoints. Match on the stable
    // `number` carried in analyse.wp1/wp2 — NOT on coordinates: greatcircle legs
    // are drawn as dense arcs (coordinates[1] is an arc sample, not the end
    // waypoint) and the drawn coords are viewport-wrapped, so a coordinate
    // string match breaks after a recalc. Insertion happens AFTER the start
    // waypoint → at its index + 1.
    const wp1Number = lineSegment?.properties?.analyse?.wp1?.properties?.number;
    if (wp1Number == null) return;
    const startIdx = features.findIndex(
      (f) => f?.properties?.number === wp1Number,
    );
    if (startIdx < 0) return;
    const minIndex = startIdx + 1;

    // Both halves keep the split leg's type, owned by the preceding original point.
    const legOwner = features
      .slice(0, minIndex)
      .findLast((f) => f?.properties?.type === "original_point");
    const splitLegtype = legtypeOf(legOwner);
    const newPoint = turf.point([e.latlng.lng, e.latlng.lat], {
      legtype: splitLegtype,
      legType: splitLegtype,
      type: "original_point",
    });

    // Carry a placeholder timestamp on the new point so downstream
    // consumers (vesselHelper.setTimeIntervalTree → flatten interval-tree)
    // don't choke on `dayjs(null).unix()` returning NaN. The recalc that
    // fires below will overwrite this with the real value within ~1s.
    const placeholderTs =
      route.points.geojson.features[minIndex - 1]?.properties?.timestamp ??
      route.points.geojson.features[minIndex]?.properties?.timestamp ??
      null;
    const newPointWithTs = {
      ...newPoint,
      properties: { ...newPoint.properties, timestamp: placeholderTs },
    };

    // Insert the new point at the optimal position
    const updatedPoints = [...route.points.geojson.features];
    updatedPoints.splice(minIndex, 0, newPointWithTs);

    // Reindex the points
    const reindexedPoints = updatedPoints.map((point, idx) => {
      return turf.point(point.geometry.coordinates, {
        ...point.properties,
        number: idx + 1,
        extended_number: 0,
      });
    });

    // Update the vesselRoutes object
    const updatedVesselRoutes = vesselRoutes.map((r) => {
      if (r === route) {
        return {
          ...r,
          points: {
            ...r.points,
            geojson: {
              ...r.points.geojson,
              features: reindexedPoints,
            },
          },
        };
      }
      return r;
    });

    // Recalc the edited draft in place with its own slot and settings.
    const editedRouteId = route?.points?.routeId ?? route?.lines?.routeId;
    const updatedRoute =
      updatedVesselRoutes.find(
        (r) => (r?.points?.routeId ?? r?.lines?.routeId) === editedRouteId,
      ) ?? updatedVesselRoutes[0];
    // Only the planner's own draft feeds the Result step and uses its fields.
    const isPlannerDraft =
      plannerDraftRouteId != null && editedRouteId === plannerDraftRouteId;

    if (!route?.sketch) {
      if (isPlannerDraft) setEtaError(null);
      runPlanrouteAnalyser({
        accessContext,
        setAccessContext,
        voyagePlannerPoints: updatedRoute.points.geojson,
        calculatorDepartureTime,
        calculatorBasespeed,
        plannerVesselSettings,
        speedProfileData,
        consumptionProfileData,
        ...resolveWeatherLimits(
          isPlannerDraft ? plannerVesselSettings : null,
          selectVesselMeta(routeState, updatedRoute?.points?.vesselId),
        ),
        cwViaAreasGeojson,
        applyRnwSafety,
        applyEca,
        avoidHra,
        avoidJwla,
        avoidCaa,
        setEtaResponse,
        setRouteConsumption,
        setPathfinderResponse,
        setEtaError,
        replaceRoute,
        setIsCalculating,
        draftRouteId: editedRouteId,
        previousJsonRequest: route?.jsonRequest ?? null,
        vesselName: updatedRoute?.points?.vesselName ?? "Pathfinder",
        ownTiming: !isPlannerDraft,
      });
    }

    updatedVesselRoutes.forEach((r) => {
      const targetRouteId =
        r?.points?.routeId ?? r?.lines?.routeId ?? null;
      if (targetRouteId == null) return;
      const fragment = normalizeRenderedRoute(targetRouteId, r);
      replaceRoute(targetRouteId, fragment);
    });

    // Remove the hover marker
    setHoverMarker(null);
  };

  return vesselRoutes === null ? null : (
    <LayerGroup>
      {vesselRoutes.map((route, idx) => {
        let routeLine = route.lines;
        // If there is no line geometry, skip rendering this route
        if (!routeLine || !routeLine.geojson) {
          return null;
        }
        // Import previews are read-only on the map (no insert-on-line).
        const editable =
          routeLine.routeType === "Pathfinder" && route?.source !== "import";
        let lineGeojson = routeLine.geojson;
        const lineGeojsonWrapped = projectLinesToViewport(
          lineGeojson,
          norm,
          true,
          false,
        );
        return Object.keys(routeLine).length === 0 ? null : (
          <React.Fragment
            key={`RouteLines-${routeLine.routeId || idx}-${mapBounds}-${viewportTick}`}
          >
            {" "}
            {/* Render the white stroke */}
            <GeoJSON
              key={`RouteLines-Stroke-${routeLine.routeId}-${mapBounds}-${JSON.stringify(
                lineGeojsonWrapped.features,
              )}-${viewportTick}`}
              interactive={!editable}
              data={lineGeojsonWrapped}
              style={(feature) =>
                getStyle({
                  feature,
                  routeType: routeLine.routeType,
                  state: routeLine.state,
                  isStroke: true,
                  sketch: route?.sketch,
                })
              }
            />
            {/* Render the colored line on top */}
            <GeoJSON
              key={`RouteLines-Line-${routeLine.routeId}-${mapBounds}-${JSON.stringify(lineGeojsonWrapped.features)}`}
              interactive={!editable}
              data={lineGeojsonWrapped}
              style={(feature) =>
                getStyle({
                  feature,
                  routeType: routeLine.routeType,
                  state: routeLine.state,
                  isStroke: false,
                  sketch: route?.sketch,
                })
              }
            />
            {/* Wide hit area: a click just off the line inserts instead of hitting the map. */}
            {editable && (
              <GeoJSON
                key={`RouteLines-Hit-${routeLine.routeId}-${mapBounds}-${JSON.stringify(lineGeojsonWrapped.features)}`}
                interactive={true}
                data={lineGeojsonWrapped}
                style={(feature) => ({
                  ...getStyle({
                    feature,
                    routeType: routeLine.routeType,
                    state: routeLine.state,
                    isStroke: false,
                  }),
                  weight: 16,
                  opacity: 0,
                  dashArray: "0",
                })}
                eventHandlers={{
                  mousemove: (e) => handleMouseMove(e, e.sourceTarget.feature),
                  mouseout: handleMouseOut,
                  click: (e) => {
                    L.DomEvent.stopPropagation(e);
                    handleLineClick(e, e.sourceTarget.feature, route);
                  },
                  contextmenu: handleMouseOut,
                }}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Render the hover marker if set */}
      {hoverMarker && (
        <Marker
          key="hoverMarker"
          position={hoverMarker}
          icon={insertWaypointIcon}
          pane={"routePointPathfinderPane"}
          interactive={false}
        />
      )}
    </LayerGroup>
  );
}
